import { Link } from '@remix-run/react'
import clsx from 'clsx'
import {
  CreatePdfDialog,
  ListingBrokers,
  ListingGallery,
  ListingMeta,
  ListingStaticMap,
} from '~/components'
import { Badge, Button } from '~/components/ui'
import { getListingTypeLabel, Listing, Organization } from '~/models'
import { fmtAddress, getPropertyTypeClassName, STATUSES } from '~/utils'
import BrokerContact from './BrokerContact'
import ListingPublicShare from './Share'

interface OffMarketProps {
  listing: Listing
  organization: Organization
  canEdit: boolean
  showBrokers: boolean
}

const OffMarket = ({
  listing,
  organization,
  canEdit,
  showBrokers,
}: OffMarketProps) => {
  return (
    <main className="px-4 py-8">
      <div className="mx-auto max-w-6xl">
        <>
          <div className="mb-4 flex justify-end gap-4">
            {canEdit && (
              <Button
                asChild
                size="sm"
                variant="outline"
                className="print:hidden">
                <Link to={`/admin/listings/${listing.slug}/edit`}>Edit</Link>
              </Button>
            )}
            <CreatePdfDialog
              listingIds={[listing._id]}
              initialTitle={listing.name}
              singleListing={true}>
              <Button size="sm" variant="outline" className="print:hidden">
                Download PDF
              </Button>
            </CreatePdfDialog>
          </div>
        </>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex items-center justify-between gap-8 sm:items-start">
            <Badge className="text-base hover:bg-primary sm:mt-1">
              {STATUSES[listing.status]}
            </Badge>
            <ListingPublicShare className="sm:hidden" listing={listing} />
          </div>
          <h1 className="text-3xl font-medium text-foreground">
            {listing.name || listing.property.address}
          </h1>
        </div>
        <div className="flex items-center justify-between">
          <p className="mb-4 mt-1 text-xl font-light text-muted-foreground">
            {fmtAddress(listing.property, {
              locationVisibility: listing.locationVisibility,
            })}
          </p>

          <ListingPublicShare
            className="hidden sm:block print:hidden"
            listing={listing}
          />
        </div>

        <div className="flex flex-col-reverse gap-8 md:flex-row">
          <div className="space-y-8 md:w-7/12 lg:w-[62%]">
            <div className="rounded-md border shadow-lg">
              <div className="break-inside-avoid overflow-hidden rounded-md">
                <ListingGallery images={listing.images} />
              </div>
            </div>

            <div className="prose max-w-none">
              <h4 className="text-xl font-medium">Overview</h4>
              <p>
                This listing is no longer available. Still interested? Use the
                form below to find out more. Do you have a membership? Click{' '}
                <Link
                  className="text-primary no-underline underline-offset-4 hover:underline"
                  viewTransition
                  to={'/login'}>
                  here
                </Link>{' '}
                to login.
              </p>
            </div>
            {!organization!.options.appraisersOnly && (
              <div className="max-w-md">
                <BrokerContact listing={listing} />
              </div>
            )}

            {showBrokers && (
              <ListingBrokers
                className="block break-inside-avoid md:hidden"
                listing={listing}
              />
            )}
          </div>
          <div className="space-y-8 md:w-5/12 lg:w-[38%]">
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <h2 className="mr-2 text-2xl font-medium">
                  {getListingTypeLabel(listing)}
                </h2>
                {listing.propertyTypes.map((value, index) => {
                  return (
                    <Badge
                      key={index}
                      className={clsx(
                        'text-base',
                        getPropertyTypeClassName(value)
                      )}>
                      <span>{value}</span>
                    </Badge>
                  )
                })}
              </div>
              {(listing.propertySubtypes || []).length > 0 && (
                <p className="mb-2 italic">
                  Secondary Uses: {listing.propertySubtypes!.join(', ')}
                </p>
              )}
              <ListingMeta
                className="text-lg"
                listing={listing}
                isOffMarket={true}
              />
            </div>
            {showBrokers && (
              <ListingBrokers
                className="hidden md:block"
                listing={listing}></ListingBrokers>
            )}
          </div>
        </div>
      </div>
      <div className="mx-auto mt-4 h-96 w-full max-w-6xl break-inside-avoid pb-8 md:mt-8 md:pb-16">
        <ListingStaticMap listing={listing} />
      </div>
    </main>
  )
}

export default OffMarket
